import { FC, useCallback, useEffect, useState } from 'react';
import {
  Box,
  DetailsAccordion,
  FormInputText,
  InputLabel,
  Link,
  ProfileImage,
  ProfileInitials,
  Skeleton,
  Svg,
  SvgBubble,
  Text,
  Tooltip,
  ViewEditField,
} from '@hopskipdrive/react-components';

import { getRiderInstructions, updateRiderInstructions } from '../../../api/services/RiderInstructions';
import { actions, Can, subjects } from '../../../contexts/AbilityContext';
import { GroupedOption } from '../../../hooks/useRiderOptions';

import { LightText, RiderNotesContainer } from './RiderAccordion.styles';

interface RiderAccordionProps {
  isFetching?: boolean;
  rider?: GroupedOption;
  waypointType: string;
  locationId: string;
  updateRiderNotes: (rider: Rider) => void;
  disableRemove?: boolean;
  onRemove: () => void;
  isReadOnly?: boolean;
}

export const RiderAccordion: FC<RiderAccordionProps> = ({
  isFetching,
  rider,
  waypointType,
  locationId,
  updateRiderNotes,
  disableRemove = false,
  onRemove,
  isReadOnly = false,
}) => {
  const [locationSpecialInstructions, setLocationSpecialInstructions] = useState<RiderLocationInstructions>();
  const [riderNotes, setRiderNotes] = useState(rider?.notes);
  const [riderInstructions, setRiderInstructions] = useState('');
  const [riderNotesError, setRiderNotesError] = useState<string | null>(null);
  const [locationSpecialInstructionsError, setLocationSpecialInstructionsError] = useState<string | null>(null);

  useEffect(() => {
    setRiderNotes(rider?.notes);
  }, [rider?.notes]);
  const instructionsAttribute = `${waypointType.replace('_', '')}Instructions`;

  const getSpecialInstructions = useCallback(async () => {
    const response = await getRiderInstructions(locationId, rider?.id as string);

    if (response?.type === 'success') {
      setLocationSpecialInstructions(response.data);
      setRiderInstructions(response.data?.[instructionsAttribute]);
    }
  }, [locationId, rider?.id]);

  const updateSpecialInstructions = async () => {
    if (!rider?.id) {
      return false;
    }

    const instructionsChanged = riderInstructions !== (locationSpecialInstructions?.[instructionsAttribute] ?? '');

    if ((riderInstructions?.length ?? 0) > 500 && instructionsChanged) {
      setLocationSpecialInstructionsError('Max. 500 characters');
      setLocationSpecialInstructionsError(null); // Clear the error message after showing it, so user can see character count again on retry

      return false;
    }
    const updateResponse = await updateRiderInstructions(
      {
        slug: rider.id,
        pickupInstructions: locationSpecialInstructions?.pickupInstructions,
        dropoffInstructions: locationSpecialInstructions?.dropoffInstructions,
        [instructionsAttribute]: riderInstructions,
      },
      locationId,
      rider.id
    );

    if (updateResponse?.type !== 'error') {
      setLocationSpecialInstructions(
        (prevSpecialInstructions) =>
          ({
            ...prevSpecialInstructions,
            [instructionsAttribute]: riderInstructions,
          } as RiderLocationInstructions)
      );
    }

    return true;
  };

  useEffect(() => {
    locationId && rider?.id && getSpecialInstructions();
  }, [getSpecialInstructions, locationId]);

  return (
    <DetailsAccordion
      disableRemove={disableRemove}
      onRemove={onRemove}
      textWhenExpanded="Hide"
      textWhenCollapsed="Show"
      iconComponent={
        (isFetching && <Skeleton variant="circular" width={30} height={30} />) ||
        (rider?.photo && rider.photo.indexOf('default_photo') === -1 ? (
          <ProfileImage size={30} imgSrc={rider.photo || ''} />
        ) : (
          <ProfileInitials key={rider?.name} size={30} fullName={rider?.name || ''} />
        ))
      }
      titleComponent={
        isFetching ? (
          <Skeleton variant="text" width={150} />
        ) : (
          <div>
            {isReadOnly ? (
              <LightText>{`${rider?.name}`}</LightText>
            ) : (
              <Link href={`/riders/${rider?.id}`} target="_blank">
                {`${rider?.name}`}
              </Link>
            )}
            <LightText>{`, ${rider?.age} yo`}</LightText>
          </div>
        )
      }
    >
      {isFetching && (
        <>
          <Box mb={4}>
            <ViewEditSkeleton>Rider Notes</ViewEditSkeleton>
          </Box>
          <Box>
            <ViewEditSkeleton>{`Special ${waypointType.replace(
              '_',
              ''
            )} instructions at this location`}</ViewEditSkeleton>
          </Box>
        </>
      )}
      {!isFetching && (
        <>
          <Box mb={4} data-testid="rider-notes">
            <Can I={actions.edit} a={subjects.rider}>
              <ViewEditField
                title={
                  <span style={{ display: 'flex', gap: 8 }}>
                    Rider Notes
                    <Tooltip
                      title="Rider notes should highlight crucial rider-specific details and actions required to enable safety and support for the rider. Notes should be concise, complete, and actionable"
                      placement="right"
                    >
                      <span>
                        <SvgBubble size="big">
                          <Svg icon="question" />
                        </SvgBubble>
                      </span>
                    </Tooltip>
                  </span>
                }
                value={
                  <RiderNotesContainer fontWeight={300} textColor="secondaryText">
                    {riderNotes || 'This Rider has no notes'}
                  </RiderNotesContainer>
                }
                onSave={async () => {
                  if ((riderNotes?.length ?? 0) > 500 && riderNotes !== (rider?.notes ?? '')) {
                    setRiderNotesError('Max. 500 characters'); // Show the error message
                    setRiderNotesError(null); // Clear the error message after showing it, so user can see character count again on retry

                    return false;
                  }
                  updateRiderNotes({
                    slug: rider?.id || '',
                    riderNotes: riderNotes || '',
                  } as unknown as Rider);

                  return true;
                }}
                onCancel={() => setRiderNotes(rider?.notes)}
                TextFieldComponent={
                  <FormInputText
                    variant="outlined"
                    placeholder="Ex: Non-speaking, sensitive to loud noise. Use child lock on door and do not play any music."
                    multiline
                    rows={3}
                    value={riderNotes}
                    onChange={(e) => setRiderNotes(e.target.value)}
                    fullWidth
                    error={(riderNotes?.length ?? 0) > 500 && riderNotes !== (rider?.notes ?? '')}
                    helperText={(() => {
                      if (riderNotesError) {
                        return riderNotesError;
                      }
                      if (!riderNotes || riderNotes.length === 0) {
                        return <span style={{ color: '#6B7280' }}>Max. 500 characters</span>;
                      }

                      return (
                        <span>
                          {riderNotes.length >= 500
                            ? `Max. ${Math.min(riderNotes.length, 500)} characters`
                            : `${Math.min(riderNotes.length, 500)}/500 characters`}
                        </span>
                      );
                    })()}
                  />
                }
                canEdit={!isReadOnly}
              />
            </Can>

            <Can not I={actions.edit} a={subjects.rider}>
              {'Rider Notes'}

              <RiderNotesContainer fontWeight={300} textColor="secondaryText">
                {riderNotes || 'This Rider has no notes'}
              </RiderNotesContainer>
            </Can>
          </Box>
          <Box data-testid="rider-special-instructions">
            {locationId ? (
              <>
                <Can I={actions.edit} a={subjects.rider}>
                  <ViewEditField
                    title={
                      <span style={{ display: 'flex', gap: 8 }}>
                        {`Rider-specific ${waypointType.replace('_', '')} instructions`}
                        <Tooltip
                          title="Rider pickup and dropoff notes should highlight crucial details about the rider's specific pickup and dropoff experience to optimize their pickup/dropoff experience. Notes  should be concise, complete, and actionable"
                          placement="right"
                        >
                          <span>
                            <SvgBubble>
                              <Svg icon="question" />
                            </SvgBubble>
                          </span>
                        </Tooltip>
                      </span>
                    }
                    value={
                      <RiderNotesContainer fontWeight={300} textColor="secondaryText">
                        {riderInstructions || 'This Rider has no special instructions'}
                      </RiderNotesContainer>
                    }
                    onSave={updateSpecialInstructions}
                    onCancel={() => setRiderInstructions(locationSpecialInstructions?.[instructionsAttribute] || '')}
                    TextFieldComponent={
                      <FormInputText
                        variant="outlined"
                        placeholder={
                          waypointType === 'pick_up'
                            ? 'Ex: Drive to Apt gate. Child uses wheelchair. Help put on vest'
                            : 'Ex: High allergy risk, no shell fish in car. Call CG if showing reaction. Must go to back building'
                        }
                        multiline
                        rows={3}
                        onChange={(e) => setRiderInstructions(e.target.value)}
                        value={riderInstructions}
                        fullWidth
                        error={
                          (riderInstructions?.length ?? 0) > 500 &&
                          riderInstructions !== (locationSpecialInstructions?.[instructionsAttribute] ?? '')
                        }
                        helperText={(() => {
                          if (locationSpecialInstructionsError) {
                            return locationSpecialInstructionsError;
                          }
                          if (!riderInstructions || riderInstructions.length === 0) {
                            return <span style={{ color: '#6B7280' }}>Max. 500 characters</span>;
                          }

                          return (
                            <span>
                              {riderInstructions.length >= 500
                                ? `Max. ${Math.min(riderInstructions.length, 500)} characters`
                                : `${Math.min(riderInstructions.length, 500)}/500 characters`}
                            </span>
                          );
                        })()}
                      />
                    }
                    canEdit={!isReadOnly}
                  />
                </Can>
                <Can not I={actions.edit} a={subjects.rider}>
                  {`Special ${waypointType.replace('_', '')} instructions at this location`}
                  <RiderNotesContainer fontWeight={300} textColor="secondaryText" variant="h3">
                    {locationSpecialInstructions?.[instructionsAttribute] || 'This Rider has no special instructions'}
                  </RiderNotesContainer>
                </Can>
              </>
            ) : (
              <InputLabel>
                <i>{`Special ${waypointType.replace('_', '')} instructions are shown after a location is selected`}</i>
              </InputLabel>
            )}
          </Box>
        </>
      )}
    </DetailsAccordion>
  );
};

const ViewEditSkeleton = ({ children }) => {
  return (
    <>
      <Box display="flex" gap="8px">
        <Text textColor="secondaryText">{children}</Text>
        <Skeleton variant="text" width={20} />
      </Box>
      <Skeleton variant="text" width={150} />
    </>
  );
};
